<template>
  <v-card title="Branch Years">
    <template v-slot:append>
      <v-icon icon="mdi-calendar-plus" @click="openModal('addBranchYear')"></v-icon>
      <v-tooltip activator="parent" location="end">Add Branch Year</v-tooltip>
    </template>
    <v-card-text>
      <v-data-table-server
        v-model="branchYears.selected"
        :headers="branchYears.headers"
        :items="branchYears.data"
        :items-per-page="branchYears.pageOptions.limit"
        item-key="id"
        :items-length="branchYears.count"
        :search="branchYears.pageOptions.q"
        @update:options="handleBranchYearChange">
        <template v-slot:[`item.action`]="{ item }">
          <v-row v-if="item" justify="end">
            <v-col cols="auto">
              <router-link
                :to="{
                  name: 'AdminBranchYearEdit',
                  params: { branchId: branch.id, yearId: item.year?.id },
                }">
                <v-icon icon="mdi-pencil">
                  <v-tooltip activator="parent" location="end">Edit Branch Year</v-tooltip>
                </v-icon></router-link
              > </v-col
            ><v-col cols="auto/">
              <div @click="openModal('removeBranchYear', item)">
                <v-icon color="error">mdi-delete </v-icon>
                <v-tooltip activator="parent" location="end">Remove Branch Year </v-tooltip>
              </div>
            </v-col></v-row
          >
        </template>
        <template v-slot:top>
          <v-text-field
            v-model="branchYears.pageOptions.q"
            class="ma-2"
            density="compact"
            placeholder="Search branch year..."
            hide-details></v-text-field>
        </template>
      </v-data-table-server>
    </v-card-text>
  </v-card>

  <v-dialog v-model="addBranchYear.isAddOpen" max-width="700px">
    <v-card>
      <v-card-title>Add Branch Year</v-card-title>
      <v-card-text>
        <v-data-table-server
          v-model="addBranchYear.selected"
          :headers="addBranchYear.headers"
          :items="addBranchYear.data"
          :items-per-page="addBranchYear.pageOptions.limit"
          item-key="id"
          show-select
          :items-length="addBranchYear.count"
          :search="addBranchYear.pageOptions.q"
          @update:options="handleAddBranchYearChange">
          <template v-slot:top>
            <v-text-field
              v-model="addBranchYear.pageOptions.q"
              class="ma-2"
              density="compact"
              placeholder="Search years..."
              hide-details></v-text-field>
          </template>
        </v-data-table-server>
      </v-card-text>
      <v-card-actions>
        <v-btn color="secondary" @click="addBranchYear.isAddOpen = false">Cancel</v-btn>
        <v-btn color="primary" @click="addBranchYears">Add</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <ConfirmDeleteComponent
    :description="`${addBranchYear.currentBranchYear.year?.year}`"
    title="Branch Year"
    item="Branch Year"
    :isOpen="addBranchYear.isRemoveOpen"
    @actionCanceled="addBranchYear.isRemoveOpen = false"
    @actionConfirmed="deleteBranchYear"></ConfirmDeleteComponent>
</template>

<script setup lang="ts">
import ConfirmDeleteComponent from '@/components/ui/ConfirmDeleteComponent.vue';
import {
  BranchDTO,
  BranchYearDTO,
  IBranchDTO,
  IBranchYearDTO,
  IPaginationDTO,
  PaginationDTO,
} from '@/dtos';
import { useUpdateStatusStore } from '@/stores/modules';
import { useBranchYearStore } from '@/stores/modules/admin';
import { computed, ref, watch } from 'vue';

import type { VDataTableServer } from 'vuetify/components';

interface Props {
  branch: IBranchDTO;
}
const props = defineProps<Props>();

const branch = ref<IBranchDTO>(new BranchDTO());
const emit = defineEmits(['snack']);

watch(
  () => props.branch,
  (newVal) => {
    if (newVal) {
      branch.value = new BranchDTO(newVal);
    }
  },
  { immediate: true },
);
const branchYearStore = useBranchYearStore();
const statusStore = useUpdateStatusStore();

const pageOptionsBranchYears = ref<IPaginationDTO>(new PaginationDTO());
const branchId = computed(() => branch.value.id); // ensure branch is ready

const { data: branchYearData, isLoading: isLoadingBranchYears } =
  branchYearStore.getAllBranchYearsQuery(branchId, pageOptionsBranchYears);

const branchYears = ref({
  pageOptions: pageOptionsBranchYears.value,
  count: computed(() => branchYearData.value?.count ?? 0),
  data: computed(() => branchYearData.value?.data ?? []),
  loading: computed(() => isLoadingBranchYears.value),
  selected: [] as string[],
  headers: [
    { title: 'Year', key: 'year.year', align: 'start', sortable: true },
    { title: 'Theme', key: 'year.theme', align: 'start', sortable: true },
    { title: '', key: 'action', align: 'end', sortable: false },
  ] as const,
});

const pageOptionsPossibleBranchYears = ref<IPaginationDTO>(new PaginationDTO());
const { data: possibleBranchYearData, isLoading: isLoadingPossibleBranchYears } =
  branchYearStore.getPossibleBranchYearsQuery(branchId, pageOptionsPossibleBranchYears);

const addBranchYear = ref({
  pageOptions: pageOptionsPossibleBranchYears.value,
  count: computed(() => possibleBranchYearData.value?.count ?? 0),
  data: computed(() => possibleBranchYearData.value?.data ?? []),
  loading: computed(() => isLoadingPossibleBranchYears.value),
  currentBranchYear: new BranchYearDTO() as IBranchYearDTO,
  isAddOpen: false,
  isRemoveOpen: false,
  selected: [] as string[],
  headers: [
    { title: 'Year', key: 'year', align: 'start', sortable: true },
    { title: 'Theme', key: 'theme', align: 'start', sortable: true },
    { title: '', key: 'action', align: 'end', sortable: false },
  ] as const,
});

const handleBranchYearChange = (value: VDataTableServer) => {
  pageOptionsBranchYears.value.buildPagination(value);
};

const handleAddBranchYearChange = (value: VDataTableServer) => {
  pageOptionsPossibleBranchYears.value.buildPagination(value);
};

const addBranchYears = async () => {
  try {
    branchYearStore.addToBranchYear.mutateAsync({
      branchId: branch.value.id,
      ids: addBranchYear.value.selected,
    });
    addBranchYear.value.selected = [];
    addBranchYear.value.isAddOpen = false;
  } catch (error) {
    console.error('Error adding branch year:', error);
  }
};

const deleteBranchYear = async () => {
  try {
    branchYearStore.removeBranchYear.mutateAsync({
      branchId: branch.value.id,
      yearId: addBranchYear.value.currentBranchYear.id,
    });
    addBranchYear.value.isRemoveOpen = false;
  } catch (error) {
    console.error('Error removing branch from year:', error);
  }
};

const openModal = (type: string, item = {}) => {
  if (type === 'addBranchYear') {
    branchYears.value.pageOptions.q = '';
    branchYears.value.selected = [];
    addBranchYear.value.isAddOpen = true;
  } else if (type === 'removeBranchYear') {
    addBranchYear.value.currentBranchYear = item as IBranchYearDTO;
    addBranchYear.value.isRemoveOpen = true;
  }
};

const status = computed(() => statusStore.getStatus(key.value));
const key = computed(() => `branch-year-${branch.value.id}`);
watch(status, (value) => {
  if (!value) return;

  emit('snack', 'Branch Year', value, statusStore.getError(key.value));
});
</script>
